/* © CNES */
import { Control } from 'ol/control';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { ConfigContext } from '../../../../context/ConfigContext';
import MapContext from '../../MapContext';
import './DrawPolygonControl.scss';

/**
 * DrawP olygon Control.
 * @param isDrawPolygonModeOn Boolean if draw mode is active.
 * @param setDrawPolygonMode Function to toggle the draw mode.
 * @returns control for map
 */
const DrawPolygonControl = ({
  isDrawPolygonModeOn,
  setDrawPolygonMode,
}: {
  isDrawPolygonModeOn: boolean;
  setDrawPolygonMode: Dispatch<SetStateAction<boolean>>;
}) => {
  const { map } = useContext(MapContext);
  const context = useContext(ConfigContext);
  let localIsDrawPolygonModeOn = useRef(isDrawPolygonModeOn);

  const toggleInteraction = useCallback(() => {
    localIsDrawPolygonModeOn.current = !localIsDrawPolygonModeOn.current;
    setDrawPolygonMode(localIsDrawPolygonModeOn.current);
  }, [setDrawPolygonMode]);

  /**
   * Add Options to draw mode on map.
   */
  useEffect(() => {
    if (!map) return;

    const button = document.createElement('button');
    button.innerHTML = `<svg width="10" height="12" viewBox="0 0 22 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.75 12.1143C1.26667 12.1143 0.854167 11.9434 0.5125 11.6018C0.170833 11.2601 0 10.8476 0 10.3643C0 9.88092 0.170833 9.46842 0.5125 9.12676C0.854167 8.78509 1.26667 8.61426 1.75 8.61426C1.83333 8.61426 1.91667 8.61842 2 8.62676C2.08333 8.63509 2.19167 8.65592 2.325 8.68926L7.325 3.68926C7.29167 3.55592 7.27083 3.44759 7.2625 3.36426C7.25417 3.28092 7.25 3.19759 7.25 3.11426C7.25 2.63092 7.42083 2.21842 7.7625 1.87676C8.10417 1.53509 8.51667 1.36426 9 1.36426C9.48333 1.36426 9.89583 1.53509 10.2375 1.87676C10.5792 2.21842 10.75 2.63092 10.75 3.11426C10.75 3.14759 10.725 3.33926 10.675 3.68926L13.425 6.43926C13.5583 6.40592 13.6667 6.38509 13.75 6.37676C13.8333 6.36842 13.9167 6.36426 14 6.36426C14.0833 6.36426 14.1667 6.36842 14.25 6.37676C14.3333 6.38509 14.4417 6.40592 14.575 6.43926L18.575 2.43926C18.5417 2.30592 18.5208 2.19759 18.5125 2.11426C18.5042 2.03092 18.5 1.94759 18.5 1.86426C18.5 1.38092 18.6708 0.968424 19.0125 0.626758C19.3542 0.285091 19.7667 0.114258 20.25 0.114258C20.7333 0.114258 21.1458 0.285091 21.4875 0.626758C21.8292 0.968424 22 1.38092 22 1.86426C22 2.34759 21.8292 2.76009 21.4875 3.10176C21.1458 3.44342 20.7333 3.61426 20.25 3.61426C20.1667 3.61426 20.0833 3.61009 20 3.60176C19.9167 3.59342 19.8083 3.57259 19.675 3.53926L15.675 7.53926C15.7083 7.67259 15.7292 7.78092 15.7375 7.86426C15.7458 7.94759 15.75 8.03092 15.75 8.11426C15.75 8.59759 15.5792 9.01009 15.2375 9.35176C14.8958 9.69342 14.4833 9.86426 14 9.86426C13.5167 9.86426 13.1042 9.69342 12.7625 9.35176C12.4208 9.01009 12.25 8.59759 12.25 8.11426C12.25 8.03092 12.2542 7.94759 12.2625 7.86426C12.2708 7.78092 12.2917 7.67259 12.325 7.53926L9.575 4.78926C9.44167 4.82259 9.33333 4.84342 9.25 4.85176C9.16667 4.86009 9.08333 4.86426 9 4.86426C8.96667 4.86426 8.775 4.83926 8.425 4.78926L3.425 9.78926C3.45833 9.92259 3.47917 10.0309 3.4875 10.1143C3.49583 10.1976 3.5 10.2809 3.5 10.3643C3.5 10.8476 3.32917 11.2601 2.9875 11.6018C2.64583 11.9434 2.23333 12.1143 1.75 12.1143Z" fill="black" />
      </svg>
      `;
    button.id = 'draw-polygon-button';
    button.title = 'Free shape AOI';
    const element = document.createElement('div');
    element.id = 'draw-polygon';
    element.className = 'draw-polygon-halfscreen ol-unselectable ol-control';
    if (context.isLeftSidePanelShown) {
      element.className = 'draw-polygon-fullscreen ol-unselectable ol-control';
    }
    element.appendChild(button);
    button.addEventListener('click', toggleInteraction, false);
    let customControl = new Control({ element });
    map.addControl(customControl);

    return () => {
      map.removeControl(customControl);
    };
  }, [map, isDrawPolygonModeOn, toggleInteraction, context]);

  return null;
};
export default DrawPolygonControl;
